import OptionButton from "@components/quiz/OptionButton"
import StickyFooter from "@components/quiz/StickyFooter"
import { CheckIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { QuizOption } from "../quizSteps"
import StepHeading from "./StepHeading"

interface Props {
  id: string
  layout: "list" | "chips"
  onAnswer: (answers: string[]) => void
  options: QuizOption[]
}

export default function MultiSelectStep({
  id,
  layout,
  onAnswer,
  options,
}: Props) {
  const [selected, setSelected] = useState<string[]>([])
  const { t } = useTranslation()

  const toggle = (option: QuizOption) =>
    setSelected((current) => {
      if (!current.includes(option.id)) return [...current, option.id]

      const removedIds = [
        option.id,
        ...(option.subOptions ?? []).map((subOption) => subOption.id),
      ]

      return current.filter((value) => !removedIds.includes(value))
    })

  const label = (optionId: string) =>
    t(`pages.onboarding.steps.${id}.options.${optionId}`)

  const chips = options.flatMap((option, index) => [
    { delayMs: 120 + index * 35, isSubOption: false, option },
    ...(selected.includes(option.id) ? (option.subOptions ?? []) : []).map(
      (subOption) => ({ delayMs: 0, isSubOption: true, option: subOption }),
    ),
  ])

  return (
    <div className="flex grow flex-col gap-6 pt-4">
      <StepHeading
        headingKey={`pages.onboarding.steps.${id}.heading`}
        subheadingKey="pages.onboarding.chooseAll"
      />

      {layout === "chips" ? (
        <div className="grid grid-cols-3 gap-2">
          {chips.map(({ delayMs, isSubOption, option }) => {
            const isSelected = selected.includes(option.id)

            return (
              <button
                aria-pressed={isSelected}
                className={`relative flex min-h-[52px] items-center justify-center rounded-vm-md border px-2 py-2 text-center text-sm font-medium leading-tight transition-[transform,background-color,border-color,color] duration-200 active:scale-95 ${
                  isSubOption ? "animate-scale-in" : "animate-fade-up"
                } ${
                  isSelected
                    ? "border-vm-primary bg-vm-primary-soft text-vm-primary-text"
                    : "border-vm-line bg-vm-surface text-vm-ink hover:border-vm-primary/40"
                }`}
                key={option.id}
                onClick={() => toggle(option)}
                style={{ animationDelay: `${delayMs}ms` }}
                type="button"
              >
                {label(option.id)}
                {isSelected && (
                  <CheckIcon className="absolute -right-1.5 -top-1.5 h-5 w-5 animate-check-pop rounded-full bg-vm-primary p-1 text-white" />
                )}
              </button>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {options.map((option, index) => (
            <OptionButton
              animationDelayMs={120 + index * 60}
              emoji={option.emoji}
              indicator="checkbox"
              isSelected={selected.includes(option.id)}
              key={option.id}
              label={label(option.id)}
              onClick={() => toggle(option)}
            />
          ))}
        </div>
      )}

      <StickyFooter>
        <button
          className="vm-button-primary"
          disabled={selected.length === 0}
          onClick={() => onAnswer(selected)}
          type="button"
        >
          {t("shared.continue")}
        </button>
      </StickyFooter>
    </div>
  )
}
